import axios from 'axios';
import type {
  PaymentConfig,
  ChainData,
  HonorItem,
  DonationOrder,
  ExpansionOrder,
  RoomPackageOrder,
  RoomCreationOrder,
  PendingManualOrder,
  CreateDonationOrderRequest,
  CreateExpansionOrderRequest,
  CreateRoomPackageOrderRequest,
  CreateRoomCreationOrderRequest,
  ConfirmOrderRequest,
  HandleManualOrderRequest
} from '../types';

const api = axios.create({
  baseURL: '/api/payment',
  headers: {
    'Content-Type': 'application/json'
  }
});

// 请求拦截器
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 响应拦截器
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// 配置相关API
export const getPaymentConfig = async () => {
  const response = await api.get<{ success: boolean; data: PaymentConfig }>('/config');
  return response.data;
};

export const updatePaymentConfig = async (config: Partial<PaymentConfig>) => {
  const response = await api.post<{ success: boolean; message: string }>('/admin/config', config);
  return response.data;
};

// 链上数据API
export const getTrc20Data = async () => {
  const response = await api.get<{ success: boolean; data: ChainData }>('/trc20');
  return response.data;
};

export const getErc20Data = async () => {
  const response = await api.get<{ success: boolean; data: ChainData }>('/erc20');
  return response.data;
};

// 荣誉榜API
export const getHonorList = async (type: 'total' | 'monthly' = 'total') => {
  const response = await api.get<{ success: boolean; data: HonorItem[] }>('/honor', { params: { type } });
  return response.data;
};

// 捐助订单API
export const createDonationOrder = async (data: CreateDonationOrderRequest) => {
  const response = await api.post<{ success: boolean; data: any; message: string }>('/donation', data);
  return response.data;
};

export const confirmDonationOrder = async (data: ConfirmOrderRequest) => {
  const response = await api.post<{ success: boolean; message: string }>('/donation/confirm', data);
  return response.data;
};

export const getDonationOrders = async (params?: { page?: number; pageSize?: number; username?: string; status?: string }) => {
  const response = await api.get<{ success: boolean; orders: DonationOrder[]; pagination: any }>('/admin/donation-orders', { params });
  return response.data;
};

export const completeDonationOrder = async (orderId: number) => {
  const response = await api.post<{ success: boolean; message: string }>('/admin/donation/complete', { orderId });
  return response.data;
};

// 扩容订单API
export const createExpansionOrder = async (data: CreateExpansionOrderRequest) => {
  const response = await api.post<{ success: boolean; data: any; message: string }>('/expansion', data);
  return response.data;
};

export const confirmExpansionOrder = async (data: ConfirmOrderRequest) => {
  const response = await api.post<{ success: boolean; message: string }>('/expansion/confirm', data);
  return response.data;
};

export const getExpansionOrders = async (params?: { page?: number; pageSize?: number; username?: string; status?: string }) => {
  const response = await api.get<{ success: boolean; orders: ExpansionOrder[]; pagination: any }>('/admin/expansion-orders', { params });
  return response.data;
};

export const completeExpansionOrder = async (orderId: number) => {
  const response = await api.post<{ success: boolean; message: string }>('/admin/expansion/complete', { orderId });
  return response.data;
};

// 人工处理订单API
export const getPendingManualOrders = async (params?: { page?: number; pageSize?: number; status?: string }) => {
  const response = await api.get<{ success: boolean; orders: PendingManualOrder[]; pagination: any }>('/admin/pending-orders', { params });
  return response.data;
};

export const handleManualOrder = async (data: HandleManualOrderRequest) => {
  const response = await api.post<{ success: boolean; message: string }>('/admin/pending/handle', data);
  return response.data;
};

// 触发自动处理
export const triggerAutoProcess = async () => {
  const response = await api.post<{ success: boolean; message: string }>('/admin/trigger-process');
  return response.data;
};

// 房间套餐订单API
export const createRoomPackageOrder = async (data: CreateRoomPackageOrderRequest) => {
  const response = await api.post<{ success: boolean; data: any; message: string }>('/room-package', data);
  return response.data;
};

export const confirmRoomPackageOrder = async (data: ConfirmOrderRequest) => {
  const response = await api.post<{ success: boolean; message: string }>('/room-package/confirm', data);
  return response.data;
};

export const getRoomPackageOrders = async (params?: { page?: number; pageSize?: number; username?: string; status?: string }) => {
  const response = await api.get<{ success: boolean; orders: RoomPackageOrder[]; pagination: any }>('/admin/room-package-orders', { params });
  return response.data;
};

export const completeRoomPackageOrder = async (orderId: number) => {
  const response = await api.post<{ success: boolean; message: string }>('/admin/room-package/complete', { orderId });
  return response.data;
};

// 房间创建订单API
export const createRoomCreationOrder = async (data: CreateRoomCreationOrderRequest) => {
  const response = await api.post<{ success: boolean; data: any; message: string }>('/room-creation', data);
  return response.data;
};

export const confirmRoomCreationOrder = async (data: ConfirmOrderRequest) => {
  const response = await api.post<{ success: boolean; message: string }>('/room-creation/confirm', data);
  return response.data;
};

export const getRoomCreationOrders = async (params?: { page?: number; pageSize?: number; username?: string; status?: string }) => {
  const response = await api.get<{ success: boolean; orders: RoomCreationOrder[]; pagination: any }>('/admin/room-creation-orders', { params });
  return response.data;
};

export const completeRoomCreationOrder = async (orderId: number) => {
  const response = await api.post<{ success: boolean; message: string }>('/admin/room-creation/complete', { orderId });
  return response.data;
};

// 链上回调（仅供内部使用）
export const handleChainCallback = async (data: any) => {
  const response = await api.post('/chain-callback', data);
  return response.data;
};

export default api;
